import React, { useState } from "react";
import { Box, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import {
  ArrowForwardIos as ArrowForwardIosIcon,
  Archive as ArchiveIcon,
} from "@mui/icons-material";
import { RiskScoreChip } from "../../components/common/RiskScoreChip";
import { useMutation } from "@apollo/client";
import { UNARCHIVE_RISK } from "../../../../../apis/organization-apis/risk-apis/riskApis";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import { useTheme } from "@mui/material";
import { EmptyFields } from "../../../../../utils/enums";

const RiskRegisterTable = ({
  risks,
  onViewDetails,
  emptyMessage = "No risks available",
  refetch,
  setSnackbarInfo,
}) => {
  const theme = useTheme();
  const [unarchiveRisk, { loading: unarchiving }] = useMutation(UNARCHIVE_RISK);

  const columns = [
    { label: "Risk ID", field: "key", align: "left", width: "10%" },
    { label: "Risk Name", field: "title", align: "left", width: "25%" },
    {
      label: "Category",
      field: "risk_category_title",
      align: "left",
      width: "15%",
    },
    {
      label: "Owner",
      field: "risk_owner_email",
      align: "left",
      width: "15%",
    },
    {
      label: "Inherent Risk",
      field: "inherent_risk_score",
      align: "left",
      width: "12.5%",
    },
    {
      label: "Calculated Residual Risk",
      field: "calculated_residual_risk_score",
      align: "left",
      width: "12.5%",
    },
    {
      label: "Controls",
      field: "controls_count",
      align: "left",
      width: "10%",
    },
    { label: "Actions", field: "actions", align: "center", width: "8%" },
  ];

  const handleUnarchive = async (row) => {
    setSnackbarInfo({
      message: "Unarchiving risk...",
      vertical: "top",
      horizontal: "right",
    });
    try {
      const { data } = await unarchiveRisk({
        variables: {
          id: row?.id,
        },
      });
      if (data?.unarchiveRisk?.success) {
        setSnackbarInfo({
          message: "Risk unarchived successfully",
          vertical: "top",
          horizontal: "right",
        });
        refetch();
      } else {
        setSnackbarInfo({
          message: "Failed to unarchive risk",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (err) {
      setSnackbarInfo({
        message: `Failed to unarchive risk: ${err.message}`,
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const renderCell = (row, column) => {
    const archivedStyle =
      row?.is_active === false
        ? {
          opacity: 0.6,
          color: theme.customColors.text.secondary,
        }
        : {};

    switch (column?.field) {
      case "inherent_risk_score": {
        const inherentScore = row?.inherent_risk_score;
        return (
          <Box sx={{ ...archivedStyle, textAlign: "left", width: "100%" }}>
            <RiskScoreChip score={inherentScore} type="inherent" />
          </Box>
        );
      }
      case "calculated_residual_risk_score": {
        const residualScore = row?.calculated_residual_risk_score;
        return (
          <Box sx={{ ...archivedStyle, textAlign: "left", width: "100%" }}>
            <RiskScoreChip score={residualScore} type="residual" />
          </Box>
        );
      }
      case "actions":
        return row?.is_active === false ? (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Tooltip title="Unarchive" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleUnarchive(row)}
                  sx={{ color: theme.customColors.icons.dark }}
                  disabled={unarchiving}
                >
                  <ArchiveIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Tooltip title="View Details" arrow>
              <IconButton
                size="small"
                onClick={() => onViewDetails(row)}
                sx={{
                  color: theme.customColors.icons.dark,
                  "&:hover": {
                    backgroundColor: theme.customColors.background.light,
                    transform: "translateX(2px)",
                    transition: "all 0.2s ease",
                  },
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      case "title":
        return (
          <Tooltip title={row?.description} arrow>
            <Typography
              variant="body2"
              sx={{
                maxWidth: "400px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                ...archivedStyle,
                textAlign: "left",
                width: "100%",
              }}
            >
              {row?.title || EmptyFields.TITLE}
            </Typography>
          </Tooltip>
        );
      case "risk_owner_email":
        return (
          <Box
            sx={{
              textAlign: "left",
              width: "100%",
              ...archivedStyle,
            }}
          >
            <Typography variant="body2">
              {row?.risk_owner_email || EmptyFields.OWNER}
            </Typography>
          </Box>
        );
      default:
        return (
          <Typography
            variant="body2"
            sx={{
              ...archivedStyle,
              textAlign: "left",
              width: "100%",
              color: theme.customColors.text.strong,
            }}
          >
            {row[column?.field]}
          </Typography>
        );
    }
  };

  if (!risks || risks?.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8, px: 4 }}>
        <Typography variant="h5" color="textSecondary" sx={{ mb: 1 }}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ReusableTable
        columns={columns}
        data={risks}
        renderCell={renderCell}
        rowsPerPageOptions={[10, 20, 50, 100]}
        height="auto"
        showPagination={true}
      />
    </>
  );
};

export default RiskRegisterTable;
